import { useMemo } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CareElementGridHelper } from "@/Flowsheets/Helpers/CareElementGridHelper";
import { FlowsheetVitalSignFilterHelper } from "@/Flowsheets/Helpers/FlowsheetVitalSignFilterHelper";
import { VitalSignCareElementGridHelper } from "@/Flowsheets/Helpers/VitalSignCareElementGridHelper";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { VitalSignCareElement } from "@/Flowsheets/Models/CareElements/VitalSignCareElement";
import { VitalSignCareElementName } from "@/Flowsheets/Models/CareElements/VitalSignCareElementName";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import { CareComponentTable, CareComponentTableColumn } from "./CareComponentParts/CareComponentTable";
import { GridCell } from "./CareComponentParts/GridCell";
import VitalSignGridTargetLabel from "./CareComponentParts/VitalSignGridTargetLabel";
import { ICareComponentProps } from "./Types";

import styles from "./VitalSignsCareComponent.module.scss";

/**
 * Interface representing the VitalSignsCareComponent state.
 */
interface VitalSignsCareComponent extends CareComponent {
    CareElements: VitalSignCareElement[];
}

const vitalLabelDictionary: Record<VitalSignCareElementName, string> = {
    [VitalSignCareElementName.BloodPressure]: "BloodPressureAbbrevLabel",
    [VitalSignCareElementName.BloodPressureSystolic]: "BloodPressureSystolicAbbrevLabel",
    [VitalSignCareElementName.BloodPressureDiastolic]: "BloodPressureDiastolicAbbrevLabel",
    [VitalSignCareElementName.BodyMassIndex]: "BMILabel",
    [VitalSignCareElementName.WaistCircumference]: "WaistCircumferenceAbbrevLabel",
    [VitalSignCareElementName.Height]: "HeightLabel",
    [VitalSignCareElementName.Weight]: "WeightLabel",
    [VitalSignCareElementName.HeartRate]: "HRLabel"
};

const vitalSignNameToUnitLabelMap: Record<string, string> = {
    [VitalSignCareElementName.BloodPressure]: "SittingLabel",
    [VitalSignCareElementName.BodyMassIndex]: "BodyMassIndexMeasurementUnitLabel",
    [VitalSignCareElementName.HeartRate]: "BeatsPerMinuteAbbrevLabel"
};

/**
 * Handles the vital label rendering.
 * @param name - The name of the vital sign.
 * @param getText - Function to retrieve localized text.
 * @returns The vital label as a string.
 */
function handleVitalLabel(name: string, getText: (key: string) => string): string {
    return vitalLabelDictionary[name] ? getText(vitalLabelDictionary[name]) : name;
}

/**
 * Handles the vital sign measurement unit label rendering.
 * @param name - The name of the vital sign.
 * @param getText - Function to retrieve localized text.
 * @returns The vital sign measurement unit label as a string.
 */
function handleVitalUnitLabel(name: string, getText: (key: string) => string): string {
    return vitalSignNameToUnitLabelMap[name] ? getText(vitalSignNameToUnitLabelMap[name]) : "";
}

function renderVitalSignCell(data: VitalSignCareElement, date: Date): JSX.Element {
    const values = data.Exams.firstOrDefault(v => new Date(v.Date).getTime() === date.getTime());

    let displayValue = "";
    let showRed = false;

    if (data.Name === VitalSignCareElementName.BloodPressure) {
        displayValue = values?.Value && values?.Value2 ? `${values.Value}/${values.Value2}` : "";
        showRed = VitalSignCareElementGridHelper.isBloodPressureOutOfRange(
            values?.Value,
            values?.Value2,
            data.TargetRange.LowerBound,
            data.TargetRange.UpperBound,
            data.Operator
        );
    }
    else {
        displayValue = values?.Value != null ? `${values.Value}${values?.Unit ?? ""}` : "";
        showRed = data.TargetRange != null && values?.Value != null
            ? CareElementGridHelper.isValueOutOfRange(values.Value, data)
            : false;
    }

    return (
        <GridCell
            id={`vitalsign-data-${data.Name}-${date.toLocaleDateString("en-CA")}`}
            value={displayValue}
            showAlert={showRed}
            alertVariant="alarming"
            dataCy={`vitalsign-data-${data.Name}-${date.toLocaleDateString("en-CA")}`}
        />
    );
}

const VitalSignsCareComponent = React.memo(
    ({ isEditable, state, mutateState, encounterDate, filterSettings }: ICareComponentProps<VitalSignsCareComponent>): JSX.Element => {
        const { getText } = useTranslation();

        const { filteredCareElements, sortedDates } = useMemo(() => {
            const filteredCareElements = FlowsheetVitalSignFilterHelper.filterCareElements(
                state.CareElements,
                filterSettings,
                encounterDate
            );

            const allExams = filteredCareElements.flatMap(el => el.Exams ?? []);
            const sortedDates = Array.from(new Set(allExams.map(exam => new Date(exam.Date).toISOString())))
                .map(dateStr => new Date(dateStr))
                .sort((a, b) => b.getTime() - a.getTime());

            return { filteredCareElements, sortedDates };
        }, [state.CareElements, filterSettings, encounterDate]);

        const dateColumns: CareComponentTableColumn[] = sortedDates.map(date => ({
            label: date.toLocaleDateString("en-CA"),
            isFixed: false,
            getKey: (data: VitalSignCareElement, index: number) => `value_${data.Name}_${index}_${date.toISOString()}`,
            render: (data: VitalSignCareElement) => renderVitalSignCell(data, date)
        }));

        const fixedColumns: CareComponentTableColumn[] = [
            {
                label: getText("VitalLabel"),
                isFixed: true,
                getKey: (data: VitalSignCareElement, index: number) => `vital_${data.Name}_${index}`,
                render: (data: VitalSignCareElement) => (
                    <>
                        <p className={styles.vitalSignName}>{handleVitalLabel(data.Name, getText)}</p>
                        <p className={styles.unit}>{handleVitalUnitLabel(data.Name, getText)}</p>
                    </>
                )
            },
            {
                label: getText("FrequencyLabel"),
                isFixed: true,
                getKey: (data: VitalSignCareElement, index: number) => `frequency_${data.Name}_${index}`,
                render: (data: VitalSignCareElement) => (
                    <GridCell
                        id={`vitalsign-frequency-${data.Name}`}
                        value={CareElementGridHelper.handleFrequencyLabel(data.Frequency, getText)}
                        showAlert={VitalSignCareElementGridHelper.shouldShowFrequencyWarningBadge(data.Frequency, encounterDate, data.Exams ?? [])}
                        alertVariant="warning"
                        alertTooltipText={getText("Flowsheets_OutsideFrequencyTooltipLabel")}
                    />
                ) },
            {
                label: getText("TargetLabel"),
                isFixed: true,
                getKey: (data: VitalSignCareElement, index: number) => `target_${data.Name}_${index}`,
                render: (data: VitalSignCareElement) => <VitalSignGridTargetLabel data={data} />
            }
        ];

        return (
            <div className={styles.vitalSignsCareComponent}>
                <CareComponentTable
                    columns={[...fixedColumns, ...dateColumns]}
                    data={filteredCareElements}
                />

                <CareComponentNotes
                    id={`vitalSignsNotes-${state.Id}`}
                    dataCy="VitalSignsNotes"
                    value={state.Note}
                    isEditable={isEditable}
                    onTextChange={newValue => mutateState(s => s.Note = newValue)}
                />
            </div>
        );
    }
);

export { VitalSignsCareComponent };
